import type { QuotaRepository } from 'features/quota/repository';

import type { CompanyCrmInfo, CompanyCrmInfoAccount } from '../../../domain/company';
import { Crm } from '../../../domain/crm';
import type { User } from '../../../domain/user';
import { queryClient } from '../../../queryClient';
import type { AppStore } from '../../../services/appStore';
import { translate } from '../../../services/i18n';
import { formatErrorMessage } from '../../../services/string';
import companyQueryKey from '../../company/companyQueryKey';
import { USER_QUERY_KEY } from '../../user/userQueryKey';
import { crmApi } from '../api';

import type { ExportAccountToCrmRepository } from './useCase';

export const makeExportAccountToCrmRepository = (
  appStore: AppStore,
  quotaRepository: QuotaRepository
): ExportAccountToCrmRepository => ({
  exportAccountToCrm: (domain: string, crm: Crm) =>
    crmApi.exportAccount(
      {
        countries: [0],
        domains: [domain],
      },
      crm
    ),
  notifyDomainExportSucceed: () => {
    appStore.setSuccessToast({
      content: translate('company.export_to_crm.success'),
    });
  },
  notifyDomainExportFailed: (error: unknown) => {
    appStore.setErrorToast({
      content:
        error instanceof Error && error.message
          ? `${translate('company.export_to_crm.failed_reason')} ${formatErrorMessage(error.message)}`
          : translate('company.export_to_crm.failed'),
    });
  },
  updateCompanyCrmInfo({ crm, domain, externalLink }: { crm: Crm; domain: string; externalLink: string }) {
    const companyCrmInfoQueryKey = companyQueryKey.companyCrmInfo(domain);
    const user = queryClient.getQueryData<User>([USER_QUERY_KEY]);

    const updateCrmAccounts = (accounts: CompanyCrmInfoAccount[]) => [
      ...accounts,
      {
        url: externalLink,
        companyName: domain,
        ownerName: `${user?.firstName || ''} ${user?.lastName || ''}`,
        type: 'lead',
      },
    ];

    let shouldDecreaseQuota = false;

    queryClient.setQueryData<CompanyCrmInfo | undefined>(
      companyCrmInfoQueryKey,
      (companyCrmInfoQueryData: CompanyCrmInfo | undefined) => {
        if (!companyCrmInfoQueryData) {
          return;
        }

        const crmAccounts =
          crm === Crm.hubspot ? companyCrmInfoQueryData.hubspotAccounts : companyCrmInfoQueryData.salesforceAccounts;
        const isRepeatedExportToCrm = crmAccounts.some((account) => account.url === externalLink);

        if (isRepeatedExportToCrm) {
          return companyCrmInfoQueryData;
        }

        shouldDecreaseQuota = true;

        if (crm === Crm.hubspot) {
          companyCrmInfoQueryData.hubspotAccounts = updateCrmAccounts(crmAccounts);
        } else if (crm === Crm.salesforce) {
          companyCrmInfoQueryData.salesforceAccounts = updateCrmAccounts(crmAccounts);
        }

        return companyCrmInfoQueryData;
      }
    );

    if (shouldDecreaseQuota) {
      quotaRepository.decreaseCrmExportsQuota(crm);
    }
  },
});
